// Shared, pure view-model helpers for the AR Aging collections dashboard.
// No React — these are unit-tested in arAgingModel.test.js and consumed by the
// dashboard container + presentational components.
//
// The filter/sort/aggregation half (pastDueOf, severityAmount, sumBuckets,
// filterCompanies, sortCompanies) is ported verbatim from the previous
// ArAgingDashboard so the exact prod semantics are preserved (property-level
// filtering with company recompute; company-level payment/activity filters).
//
// The collections half (healthOf, sparkSegments, ptpTags, autoNote, csvRows)
// ports the 15th-run design prototype, mapping its bucket keys (b30/b60/b90/
// b90p) onto our real payload keys (age30/age60/age90/age90Plus).

const DAY_MS = 24 * 60 * 60 * 1000

export const pastDueOf = (buckets) => Math.max(0, ((buckets && buckets.total) || 0) - ((buckets && buckets.current) || 0))

export const currencyFormat = (num) => {
  const value = Number.isFinite(Number(num)) ? Number(num) : 0
  return '$' + value.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
}

// Rounded, no-decimals variant for compact sub-lines / tags (design fmt0).
export const currencyFormat0 = (num) => {
  const value = Number.isFinite(Number(num)) ? Math.round(Number(num)) : 0
  return '$' + value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
}

// Whole days between `value` and `nowMs` (positive = in the past). null if no date.
export const daysAgo = (value, nowMs = Date.now()) => {
  if (!value) return null
  const ms = value instanceof Date ? value.getTime() : Date.parse(value)
  if (Number.isNaN(ms)) return null
  return Math.floor((nowMs - ms) / DAY_MS)
}

export const severityAmount = (buckets, severity) => {
  if (severity === '30') return buckets.age30 + buckets.age60 + buckets.age90 + buckets.age90Plus
  if (severity === '60') return buckets.age60 + buckets.age90 + buckets.age90Plus
  if (severity === '90') return buckets.age90 + buckets.age90Plus
  return buckets.total
}

export const sumBuckets = (properties) => properties.reduce((acc, property) => {
  for (const key of Object.keys(acc)) acc[key] += property.buckets[key] || 0
  return acc
}, { current: 0, age30: 0, age60: 0, age90: 0, age90Plus: 0, total: 0 })

// Company-level: keep companies whose most recent payment is at least N days
// old (never-paid counts as infinitely old), or exactly the never-paid set.
const passesLastPaymentFilter = (company, lastPayment, nowMs) => {
  if (!lastPayment) return true
  const lastDate = company.paymentStats?.lastPaymentDate ? new Date(company.paymentStats.lastPaymentDate) : null
  if (lastPayment === 'never') return !lastDate
  if (!lastDate || Number.isNaN(lastDate.getTime())) return true
  return (nowMs - lastDate.getTime()) >= Number(lastPayment) * DAY_MS
}

// Same shape for collections activity recency (lastActivityAt via overlay).
const passesLastActivityFilter = (company, lastActivity, nowMs) => {
  if (!lastActivity) return true
  const lastDate = company.lastActivityAt ? new Date(company.lastActivityAt) : null
  if (lastActivity === 'never') return !lastDate
  if (!lastDate || Number.isNaN(lastDate.getTime())) return true
  return (nowMs - lastDate.getTime()) >= Number(lastActivity) * DAY_MS
}

// PROPERTY-level filtering: each filter keeps/drops individual properties, company
// aggregate rows are recomputed from the surviving properties (and dropped when
// empty), so the visible totals always describe "what falls in the parameters".
// A search hit on the company name keeps that company's whole property list.
// last-payment / last-activity / paused are the exceptions — whole-company keeps.
export const filterCompanies = (companies, filters, nowMs = Date.now()) => {
  const search = (filters.search || '').trim().toLowerCase()
  const minBalance = filters.minBalance === '' || filters.minBalance == null ? null : Number(filters.minBalance)
  const branchId = filters.branchId === '' || filters.branchId == null ? null : Number(filters.branchId)

  return (companies || []).map(company => {
    if (!passesLastPaymentFilter(company, filters.lastPayment, nowMs)) return null
    if (!passesLastActivityFilter(company, filters.lastActivity, nowMs)) return null
    if (filters.pausedOnly && !company.pauseRequested) return null
    const companyNameHit = search && company.companyName.toLowerCase().includes(search)
    const properties = company.properties.filter(property => {
      if (filters.bucket && !(property.buckets[filters.bucket] > 0)) return false
      if (filters.pastDueOnly && pastDueOf(property.buckets) <= 0) return false
      if (filters.severity !== 'all' && severityAmount(property.buckets, filters.severity) <= 0) return false
      if (minBalance !== null && Number.isFinite(minBalance) && pastDueOf(property.buckets) < minBalance) return false
      if (branchId !== null && property.branchId !== branchId) return false
      if (search && !companyNameHit) {
        const haystack = [
          property.propertyName,
          ...(property.opportunities || []).map(opp => opp.opportunityName),
          ...(property.detailEntries || []).map(entry => entry.arNumber),
          ...(property.opportunities || []).flatMap(opp => (opp.invoices || []).map(inv => inv.arNumber))
        ].filter(Boolean).join(' ').toLowerCase()
        if (!haystack.includes(search)) return false
      }
      return true
    })
    if (!properties.length) return null
    return { ...company, properties, buckets: sumBuckets(properties) }
  }).filter(Boolean)
}

// 3-tier collections health (design health()). rank drives the attention sort.
// paused → pauseRequested; ptp broken when its due date is in the past.
export const healthOf = (company, buckets, ptp) => {
  if (company.escalated) return { label: 'Escalated', tone: 'bad', rank: 0 }
  const broken = ptp && ptpDaysRemaining(ptp) < 0
  if (company.pauseRequested || buckets.age90Plus > 0 || buckets.age90 > 0 || broken) {
    return { label: 'Needs attention', tone: 'warn', rank: 1 }
  }
  return { label: 'On track', tone: 'primary', rank: 2 }
}

// Attention-first default: escalated → needs attention → on track, then past due desc.
export const sortCompanies = (companies, sort, ptpByCompany = {}) => {
  const factor = sort.dir === 'desc' ? -1 : 1
  const ptpFor = company => ptpByCompany[company.companyId]
  const rankOf = company => healthOf(company, company.buckets, ptpFor(company)).rank
  return [...companies].sort((a, b) => {
    if (sort.key === 'name') return factor * a.companyName.localeCompare(b.companyName)
    if (sort.key === 'pastDue') return factor * (pastDueOf(a.buckets) - pastDueOf(b.buckets))
    // attention default (dir ignored)
    return (rankOf(a) - rankOf(b)) || (pastDueOf(b.buckets) - pastDueOf(a.buckets))
  })
}

// Header sort cycle: click a key → its default dir; again → flip; again → reset
// to attention-first. name defaults asc, pastDue defaults desc.
export const cycleSort = (sort, key) => {
  const defaultDir = key === 'name' ? 'asc' : 'desc'
  if (sort.key !== key) return { key, dir: defaultDir }
  if (sort.dir === defaultDir) return { key, dir: defaultDir === 'asc' ? 'desc' : 'asc' }
  return { key: 'attention', dir: 'desc' }
}

// Aging spark-bar: one segment per non-empty past-due bucket, width = its share
// of past due (floored at 5%). current is excluded. Empty → single primary fill.
const SPARK_SEGMENTS = [
  { key: 'age30', tone: 'line-2' },
  { key: 'age60', tone: 'warn' },
  { key: 'age90', tone: 'accent' },
  { key: 'age90Plus', tone: 'danger' }
]
export const sparkSegments = (buckets) => {
  const pastDue = pastDueOf(buckets)
  if (pastDue <= 0) return [{ tone: 'primary-soft', pct: 100 }]
  return SPARK_SEGMENTS
    .filter(segment => buckets[segment.key] > 0)
    .map(segment => ({ tone: segment.tone, pct: Math.max(5, Math.round((buckets[segment.key] / pastDue) * 100)) }))
}

// Signed days until the PTP due date (positive = future promise, negative = overdue).
export const ptpDaysRemaining = (ptp, nowMs = Date.now()) => {
  if (!ptp) return null
  if (Number.isFinite(ptp.inDays)) return ptp.inDays // prototype-style relative form
  if (ptp.dueDate) {
    const ms = Date.parse(ptp.dueDate)
    if (!Number.isNaN(ms)) return Math.round((ms - nowMs) / DAY_MS)
  }
  return null
}

const dateFromDaysAhead = (days, nowMs = Date.now()) => {
  const date = new Date(nowMs + days * DAY_MS)
  return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}`
}

// Row/panel tags: PTP (future = info, broken = danger) plus a QUIET tag when
// past due and untouched for ≥14 days.
export const ptpTags = (company, ptp, lastActivityDays, nowMs = Date.now()) => {
  const tags = []
  const remaining = ptpDaysRemaining(ptp, nowMs)
  if (ptp && remaining != null) {
    if (remaining < 0) {
      tags.push({ text: `PTP BROKEN — DUE ${dateFromDaysAhead(remaining, nowMs)}`, tone: 'danger' })
    } else {
      tags.push({ text: `PTP ${currencyFormat0(ptp.amount)} BY ${dateFromDaysAhead(remaining, nowMs)}`, tone: 'info' })
    }
  }
  if (pastDueOf(company.buckets) > 0 && lastActivityDays != null && lastActivityDays >= 14) {
    tags.push({ text: `QUIET ${lastActivityDays}D`, tone: 'warn' })
  }
  return tags
}

// Sub-line preview of what the 15th run will do for this company.
export const autoNote = (company, buckets, { excluded = false, runDate = 'Aug 15' } = {}) => {
  if (company.escalated) return 'HELD FROM RUN — ESCALATED'
  if (excluded) return 'EXCLUDED FROM THE 15TH RUN'
  const pastDue = pastDueOf(buckets)
  if (pastDue <= 0) return 'NO STATEMENT — NOTHING PAST DUE'
  return `STMT ${runDate.toUpperCase()} · ${currencyFormat0(pastDue)}`
}

// CSV of the filtered set. Columns per the design handoff.
const CSV_HEADER = ['Company', 'Health', 'Contacts', 'Past due', '1-30', '31-60', '61-90', '90+', 'Total open', 'Avg days to pay', 'Last payment']
const csvCell = (value) => {
  const text = String(value == null ? '' : value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}
export const csvRows = (companies, ptpByCompany = {}) => {
  const lines = [CSV_HEADER.map(csvCell).join(',')]
  for (const company of companies) {
    const buckets = company.buckets
    const health = healthOf(company, buckets, ptpByCompany[company.companyId]).label
    const contacts = (company.billingContacts || []).map(c => c.email || c.name).filter(Boolean).join('; ')
    const lastPay = company.paymentStats?.lastPaymentDate
      ? new Date(company.paymentStats.lastPaymentDate).toLocaleDateString('en-US')
      : 'never'
    const avg = Number.isFinite(company.paymentStats?.avgDaysToPay) ? company.paymentStats.avgDaysToPay : ''
    lines.push([
      company.companyName, health, contacts,
      pastDueOf(buckets).toFixed(2),
      (buckets.age30 || 0).toFixed(2), (buckets.age60 || 0).toFixed(2),
      (buckets.age90 || 0).toFixed(2), (buckets.age90Plus || 0).toFixed(2),
      (buckets.total || 0).toFixed(2), avg, lastPay
    ].map(csvCell).join(','))
  }
  return lines.join('\n')
}
